import React, { useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import ImageThumbnail from './ImageThumbnail';
import { IProps } from './ImageThumbnail';
import Grid from '@mui/material/Grid';

const ImageThumbnailDropTarget = ({ children, onDrop, isSelected, borderStyle }) => {
    const [{ isOver }, drop] = useDrop(() => ({
        accept: "Thumbnail",
        drop: (item) => {
            onDrop(item);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    }));

    return (
        <div 
            ref={drop} 
            className={`thumbnail thumbnail-custom ${isSelected ? 'selected' : ''}`}
            style={{ border: isOver ? borderStyle : "" }}
        >
            {children}
        </div>
    );
};

interface IDraggableImageThumbnail extends IProps {
    ItemType: string;
    draggedImageCategory: number;
    setDraggedImageCategory(category: number): void;
    updateItemSortIdx(droppedItem: number, dropTargetImageId: number, idxOffset: number, category: number): void;
    lastThumb: boolean;
    draggable: boolean;
    canReaprove: boolean;
}

const DraggableImageThumbnail: React.FC<IDraggableImageThumbnail> = ({ ...props }) => {
    const { ItemType } = props;
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemType,
        item: { id: props.image.id },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }));

    const category = props.image.overrideProperties
        ? props.image.overrideProperties.categoryId
        : props.image.categoryId;

    useEffect(() => {
        if (isDragging) {
            props.setDraggedImageCategory(category);
        } else {
            props.setDraggedImageCategory(null);
        }
    }, [isDragging]);

    const handleDropLeftDA = (item) => {
        props.updateItemSortIdx(item.id, props.image.id, 0, category);
    };
    
    const handleDropRightDA = (item) => {
        props.updateItemSortIdx(item.id, props.image.id, 1, category);
    };

    const [{ isOverLeftSpacing }, leftSpacing] = useDrop(() => ({
        accept: ItemType,
        drop: handleDropLeftDA,
        collect: (monitor) => ({
            isOverLeftSpacing: monitor.isOver(),
        }),
    }));

    const [{ isOverRightSpacing }, rightSpacing] = useDrop(() => ({
        accept: ItemType,
        drop: handleDropRightDA,
        collect: (monitor) => ({
            isOverRightSpacing: monitor.isOver(),
        }),
    }));

    const [{ isOverLeftDA }, dropLeftDA] = useDrop(() => ({
        accept: ItemType,
        drop: handleDropLeftDA,
        collect: (monitor) => ({
            isOverLeftDA: monitor.isOver(),
        }),
    }));

    const [{ isOverRightDA }, dropRightDA] = useDrop(() => ({
        accept: ItemType,
        drop: handleDropRightDA,
        collect: (monitor) => ({
            isOverRightDA: monitor.isOver(),
        }),
    }));

    const leftDropAreaStyles = {
        display: isOverLeftSpacing || isOverLeftDA && !isDragging ? "flex" : "none",
    };

    const rightDropAreaStyles = {
        display: isOverRightSpacing || isOverRightDA || props.lastThumb && !isDragging ? "flex" : "none",
    };

    const borderStyle = `2px dashed ${props.draggedImageCategory === category ? "green" : "red"}`;

    const DASize = {
        width: "100%", 
        height: "92%"
    }

    const thumbnailStyles = {
        transform: "translate3d(0, 0, 0)",
        opacity: isDragging ? 0.5 : 1,
        width: "100%",
    };

    const spacingStyle = {
        width: "18px"
    }

    const dragRefProp = {
        ref: drag
    }

    const handleDrop = (item) => {
        props.handleSwap(item.id, props.image.id);
    };

    return (
        <>
            <Grid item xs={12} sm={6} md={4} lg={3} style={leftDropAreaStyles} ref={dropLeftDA}>
                <div style={spacingStyle} /> 
                <div style={{ ...DASize, border: isOverLeftDA || isOverLeftSpacing ? borderStyle : "" }} />
                <div style={spacingStyle} />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3} style={{ display: "flex" }}>
                <div style={spacingStyle} {...isDragging ? {} : {ref: leftSpacing}} />

                <div style={thumbnailStyles} {...props.draggable ? dragRefProp : {}}>
                    <ImageThumbnailDropTarget onDrop={handleDrop} isSelected={props.imageIsSelected} borderStyle={borderStyle}>
                        <ImageThumbnail {...props} />
                    </ImageThumbnailDropTarget>
                </div>

                <div style={spacingStyle} {...isDragging ? {} : {ref: rightSpacing}} />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={3} style={rightDropAreaStyles} ref={dropRightDA}>
                <div style={spacingStyle} />
                <div style={{ ...DASize, border: isOverRightDA || isOverRightSpacing ? borderStyle : "" }} />
                <div style={spacingStyle} />
            </Grid>
        </>
    );
};

export default DraggableImageThumbnail;